/**
 * GeneralSettingsTab — Ustawienia → „Ogólne”: ustawienia całego tenanta; dziś strefa czasowa.
 *
 * Strefa tenanta (API i hooki: `lib/settings/time-zone-api.ts`) to strefa, w której liczy logika:
 * data dokumentu „dziś”, numeracja (rok/miesiąc), terminy w mailach, harmonogram KSeF. Godziny
 * na ekranach każdy użytkownik widzi według zegara swojego komputera.
 *
 * Zakładka widoczna dla każdego zalogowanego użytkownika tenanta. Zmienić strefę może tylko osoba,
 * której serwer zwrócił `canManage` (super-user albo `crm_core.time_zone.manage`) — inni widzą pole
 * tylko do odczytu i informację o braku uprawnienia. „Zapisz” jest aktywny tylko po zmianie;
 * wynik zapisu przez `useToast()`.
 */

import { type ReactElement, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { SearchableSelect, type SearchableSelectOption } from '@/components/SearchableSelect';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import {
  isInvalidTimeZoneError,
  isPermissionDeniedError,
  useTenantTimeZoneSetting,
  useUpdateTenantTimeZone,
} from '@/lib/settings/time-zone-api';
import { buildTimeZoneOptions } from '@/lib/settings/time-zone-options';

export function GeneralSettingsTab(): ReactElement {
  const { t } = useTranslation();
  const settingQ = useTenantTimeZoneSetting();

  return (
    <div className="max-w-2xl" data-testid="general-settings-tab">
      <Card padding="md">
        <h2 className="mb-1 text-base font-semibold text-text-primary">
          {t('tenant.generalSettings.timeZone.title')}
        </h2>
        {settingQ.isPending ? (
          <div className="flex justify-center py-6" data-testid="general-settings-loading">
            <Spinner />
          </div>
        ) : settingQ.isError ? (
          <Alert
            tone="error"
            actions={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void settingQ.refetch()}
                data-testid="general-settings-retry"
              >
                {t('tenant.generalSettings.retry')}
              </Button>
            }
            data-testid="general-settings-load-error"
          >
            {t('tenant.generalSettings.loadError')}
          </Alert>
        ) : (
          <TimeZoneForm
            // Nowa wartość z serwera (np. po zapisie) zaczyna formularz od nowa.
            key={settingQ.data.timeZone}
            savedTimeZone={settingQ.data.timeZone}
            canManage={settingQ.data.canManage}
          />
        )}
      </Card>
    </div>
  );
}

interface TimeZoneFormProps {
  savedTimeZone: string;
  canManage: boolean;
}

function TimeZoneForm({ savedTimeZone, canManage }: TimeZoneFormProps): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const labelId = useId();
  const [selected, setSelected] = useState<string>(savedTimeZone);
  const updateMutation = useUpdateTenantTimeZone();

  const options: SearchableSelectOption[] = useMemo(
    () =>
      buildTimeZoneOptions([savedTimeZone]).map((option) => ({
        value: option.value,
        label: option.label,
        // „Europe/Warsaw” znajdzie się też po „warsaw” czy „europe warsaw”.
        searchText: option.value.replace(/[/_]/g, ' '),
      })),
    [savedTimeZone],
  );

  const dirty = selected !== savedTimeZone;
  const saving = updateMutation.isPending;

  // `mutateAsync`, nie `mutate` z callbackami: po udanym zapisie świeża strefa z serwera
  // przemontowuje formularz (`key`), a callbacki `mutate` odłączonego komponentu by nie zadziałały.
  const handleSave = async () => {
    if (!dirty || saving) return;
    try {
      await updateMutation.mutateAsync(selected);
      toast.show({ variant: 'success', title: t('tenant.generalSettings.timeZone.saved') });
    } catch (err) {
      const messageKey = isInvalidTimeZoneError(err)
        ? 'tenant.generalSettings.timeZone.invalid'
        : isPermissionDeniedError(err)
          ? 'tenant.generalSettings.timeZone.noPermission'
          : 'tenant.generalSettings.timeZone.saveError';
      toast.show({ variant: 'error', title: t(messageKey) });
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <span id={labelId} className="mb-1 block text-sm font-medium text-text-primary">
          {t('tenant.generalSettings.timeZone.label')}
        </span>
        <SearchableSelect
          value={selected}
          onChange={(value) => {
            if (value) setSelected(value);
          }}
          options={options}
          disabled={!canManage || saving}
          aria-labelledby={labelId}
          data-testid="general-settings-time-zone"
        />
        <p className="mt-2 text-sm text-text-secondary">
          {t('tenant.generalSettings.timeZone.description')}
        </p>
      </div>

      {canManage ? (
        <div className="flex justify-end">
          <Button
            onClick={() => void handleSave()}
            disabled={!dirty || saving}
            loading={saving}
            data-testid="general-settings-time-zone-save"
          >
            {t('tenant.generalSettings.save')}
          </Button>
        </div>
      ) : (
        <Alert tone="info" data-testid="general-settings-time-zone-read-only">
          {t('tenant.generalSettings.timeZone.readOnly')}
        </Alert>
      )}
    </div>
  );
}
